import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'

import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Stack from '@mui/material/Stack'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import FormControlLabel from '@mui/material/FormControlLabel'
import RadioGroup from '@mui/material/RadioGroup'
import Radio from '@mui/material/Radio'
import Button from '@mui/material/Button'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import LoadingButton from '@mui/lab/LoadingButton'

import { Inventory } from '../../types/inventory'
import { get, post } from '../../utils/service'
import Alert from '../alert/alert'
import { Log } from '../../types/log'
import { Customer } from '../../types/customer'
import { toYearMonthString } from '../../utils/dateUtils'
import { getFullscreenElement } from '../../utils/graphUtils'
import UpdateInventoryAlert from '../logs/updateInventoryAlert'
import { getBigWeightSuffix, kgToPounds, weightInputToDecimal, weightValueToDisplay } from '../../utils/weightUtils'

enum BeanType {
  FreeText = 'freetext',
  Inventory = 'inventory',
  None = 'none',
}

interface InventorySelectorProps {
  customer: Customer
  log: Log
  disableArchivedInventory?: boolean
  onClose: () => void
  onSaveSuccess: (newLogData: Log) => void
}

const InventorySelector = (props: InventorySelectorProps): JSX.Element => {
  const { t } = useTranslation()

  const [beanType, setBeanType] = React.useState<BeanType>(
    props.log.inventory_id ? BeanType.Inventory : BeanType.FreeText
  )
  const [selectedInventoryId, setSelectedInventoryId] = React.useState<number>(props.log.inventory_id || 0)
  const [searchString, setSearchString] = React.useState<string>('')
  const [debouncedValue] = useDebounce(searchString, 250)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const [freeTextValue, setFreeTextValue] = React.useState<string>(props.log.bean_name || '')

  const [inventoryOptions, setInventoryOptions] = React.useState<Array<Inventory> | null>(null)
  const [loadInventoryUrl, setLoadInventoryUrl] = React.useState<string>('')
  const [isLoadingInventory, setIsLoadingInventory] = React.useState<boolean>(false)
  const [hasInventoryError, setHasInventoryError] = React.useState<boolean>(false)
  const [hasLogUpdateError, setHasLogUpdateError] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (props.customer.id) {
      const searchFragment = debouncedValue ? `&search=${debouncedValue}` : ''
      setLoadInventoryUrl(`inventories/?customer=${props.customer.id}${searchFragment}&page_size=all`)
      setInventoryOptions(null)
    }
  }, [props.customer.id, debouncedValue])

  React.useEffect(() => {
    if (loadInventoryUrl) {
      setIsLoadingInventory(true)
      get<Array<Inventory>>(loadInventoryUrl)
        .then((response) => {
          setIsLoadingInventory(false)
          setInventoryOptions(response.data)
        })
        .catch(() => {
          setIsLoadingInventory(false)
          setHasInventoryError(true)
        })
    }
  }, [loadInventoryUrl])

  const saveInventoryInfo = (): void => {
    setIsSaving(true)
    setHasLogUpdateError(false)

    const saveData: { bean_name: string | null; inventory_id: number | null } = { bean_name: null, inventory_id: null }
    if (beanType === BeanType.FreeText) {
      saveData.bean_name = freeTextValue
      saveData.inventory_id = null
    } else if (beanType === BeanType.Inventory) {
      saveData.bean_name = null
      saveData.inventory_id = selectedInventoryId || null
    }

    post<{ bean_name: string | null; inventory_id: number | null }, Log>(`${props.log.url}inventory/`, saveData)
      .then((savedLogData) => props.onSaveSuccess(savedLogData.data))
      .catch(() => {
        setIsSaving(false)
        setHasLogUpdateError(true)
      })
  }

  const generateOptionElement = (option: Inventory): JSX.Element => {
    const weightString =
      option.current_weight > 1000
        ? `${weightInputToDecimal(kgToPounds(option.current_weight / 1000))}${getBigWeightSuffix()}`
        : weightValueToDisplay(option.current_weight)
    return (
      <Box>
        {option.name}
        <Typography variant="caption" sx={{ pl: 1 }}>{`(${weightString} ${t('logview.left')})`}</Typography>
      </Box>
    )
  }

  return (
    <Dialog open={true} onClose={props.onClose} container={getFullscreenElement() || document.body}>
      <DialogContent sx={{ width: { xs: 300, sm: 400 } }}>
        <Stack>
          <FormControl component="fieldset" sx={{ minHeight: 205 }}>
            <FormLabel component="legend">{t('logview.setbean')}</FormLabel>
            <RadioGroup
              aria-label={t('logview.setbean')}
              defaultValue={BeanType.FreeText}
              name="adjust-values-buttons-group"
              value={beanType}
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                setBeanType(event.target.value as BeanType)
              }}
            >
              <FormControlLabel value={BeanType.FreeText} control={<Radio />} label={t('logview.freetext')} />
              {beanType === BeanType.FreeText && (
                <Stack direction="row" sx={{ ml: 1, pl: 2, borderLeft: '4px solid', borderColor: 'primary.light' }}>
                  <TextField
                    label=""
                    fullWidth
                    autoFocus
                    value={freeTextValue}
                    onChange={(event) => setFreeTextValue(event.target.value)}
                    onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                      if (event.key === 'Enter' && freeTextValue.trim()) {
                        saveInventoryInfo()
                      }
                    }}
                  />
                </Stack>
              )}
              <FormControlLabel value={BeanType.Inventory} control={<Radio />} label={t('logview.selectinventory')} />
              {beanType === BeanType.Inventory && (
                <Stack direction="row" sx={{ ml: 1, pl: 2, borderLeft: '4px solid', borderColor: 'primary.light' }}>
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={(inventoryOptions || []).filter((x) => !(props.disableArchivedInventory && x.is_archived))}
                    loading={isLoadingInventory}
                    filterOptions={(x) => x}
                    renderOption={(props, option) => (
                      <li {...props} key={option.url}>
                        {generateOptionElement(option)}
                      </li>
                    )}
                    value={(inventoryOptions || []).find((x) => x.id === selectedInventoryId) || null}
                    onChange={(_event, newValue: Inventory | null) => setSelectedInventoryId(newValue?.id || 0)}
                    isOptionEqualToValue={(option: Inventory, value: Inventory) => option.url === value.url}
                    groupBy={(option) => toYearMonthString(new Date(option.reg_date || ''))}
                    getOptionLabel={(option): string => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        onChange={(event) => {
                          setSearchString(event.target.value)
                          setSelectedInventoryId(0)
                        }}
                        label=""
                        autoFocus
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingInventory ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                          const value = (inventoryOptions || []).find((x) => x.id === selectedInventoryId) || null
                          if (event.key === 'Enter' && value) {
                            saveInventoryInfo()
                          }
                        }}
                      />
                    )}
                  />
                </Stack>
              )}
              <FormControlLabel value={BeanType.None} control={<Radio />} label={t('logview.none')} />
              {hasInventoryError && <Alert severity="error">{t('logview.loadinventoryerror')}</Alert>}
              {hasLogUpdateError && <Alert severity="error">{t('logview.setbeanerror')}</Alert>}
              <UpdateInventoryAlert
                oldLog={props.log}
                patchedLog={{
                  ...props.log,
                  inventory: inventoryOptions?.find((x) => x.id === selectedInventoryId)?.url || '',
                  inventory_id: beanType === BeanType.Inventory ? selectedInventoryId : 0,
                  inventory_name: inventoryOptions?.find((x) => x.id === selectedInventoryId)?.name,
                  inventory_current_weight: inventoryOptions?.find((x) => x.id === selectedInventoryId)?.current_weight,
                }}
              />
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>{t('common.cancel')}</Button>
        <LoadingButton
          disabled={
            (beanType === BeanType.Inventory && !selectedInventoryId) ||
            (beanType === BeanType.FreeText && !freeTextValue.trim())
          }
          variant="contained"
          loading={isSaving}
          onClick={saveInventoryInfo}
        >
          {t('logview.setbean')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default InventorySelector
